import { useCreateCustomColormap, useUpdateCustomColormap } from "@/api/hooks/customColormaps";
import { TooltipWrapper } from "@/components/molecules/TooltipWrapper/TooltipWrapper";
import { Button, ButtonBar } from "@/overlay/components/CommonParts/buttons";
import { isValidColorValue } from "@/utility/color";
import { classNames } from "@/utility/jsx";
import { t } from "@lingui/core/macro";
import type { ColormapCreate } from "@mm/metx-workbench.meteomatics.com";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import styles from "../CommonParts/CustomInput/style.module.scss";
import { ColorPickerInput } from "../LayerStackWindow/LayerFormElements";
import "./CustomColormapsManager.scss";

type ColormapMode = "segmented" | "continuous";

type CustomColormapsEditorProps = {
  selectedColormapId?: string;
  colormap: ColormapCreate;
  setColormap: Dispatch<SetStateAction<ColormapCreate>>;
  onSave: (newColormapId: string) => void;
  activeTab: string;
};

const makeId = () => crypto.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;

const normalizeStops = (stops: ColormapCreate["stops"]): ColormapCreate["stops"] =>
  stops.map((stop) => [Number(stop[0]), String(stop[1])] as ColormapCreate["stops"][number]);

const areStopsEqual = (a: ColormapCreate["stops"], b: ColormapCreate["stops"]): boolean => {
  if (a.length !== b.length) {
    return false;
  }

  return a.every((stop, index) => {
    const next = b[index];
    if (!next) {
      return false;
    }
    return Object.is(stop[0], next[0]) && String(stop[1]) === String(next[1]);
  });
};

const sortStopsWithIdsFn = (
  stops: ColormapCreate["stops"],
  ids: string[],
  direction: "asc" | "desc",
): { stops: ColormapCreate["stops"]; ids: string[] } => {
  const paired = stops.map((stop, index) => ({
    stop,
    id: ids[index] ?? makeId(),
  }));
  paired.sort((a, b) => {
    const aVal = Number(a.stop[0]);
    const bVal = Number(b.stop[0]);
    return direction === "asc" ? aVal - bVal : bVal - aVal;
  });
  return {
    stops: paired.map((p) => p.stop),
    ids: paired.map((p) => p.id),
  };
};

export function CustomColormapsEditor({
  selectedColormapId,
  onSave,
  colormap,
  setColormap,
  activeTab,
}: CustomColormapsEditorProps) {
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const stopIdsRef = useRef<string[]>(colormap.stops.map(() => makeId()));
  const stepsListRef = useRef<HTMLDivElement | null>(null);
  const prevStopsLengthRef = useRef<number>(colormap.stops.length);
  const prevSelectedColormapIdRef = useRef<string | undefined>(selectedColormapId);
  const localEditRef = useRef(false);
  const skipAutoScrollRef = useRef(false);
  const [draftStopValues, setDraftStopValues] = useState<Record<string, string>>({});

  const isLabelInvalid = colormap.label.trim().length < 1;
  const areStopsInvalid = colormap.stops.length < 2;
  const hasInvalidStopValue = colormap.stops.some(([value]) => !Number.isFinite(value));
  const hasInvalidStopColor = colormap.stops.some(([, color]) => !isValidColorValue(String(color)));
  const hasInvalidStops = areStopsInvalid || hasInvalidStopValue || hasInvalidStopColor;

  const { mutateAsync: createColormap, isLoading: isCreating } = useCreateCustomColormap();
  const { mutateAsync: updateColormap, isLoading: isUpdating } = useUpdateCustomColormap();

  const applyStopsUpdate = useCallback(
    (updater: (prevStops: ColormapCreate["stops"]) => ColormapCreate["stops"]) => {
      localEditRef.current = true;
      setColormap((prev) => ({ ...prev, stops: updater(prev.stops) }));
    },
    [setColormap],
  );

  const applyStopsUpdateSorted = useCallback(
    (updater: (prevStops: ColormapCreate["stops"]) => ColormapCreate["stops"]) => {
      localEditRef.current = true;
      setColormap((prev) => {
        const nextStopsRaw = updater(prev.stops);
        const { stops: sortedStops, ids: sortedIds } = sortStopsWithIdsFn(nextStopsRaw, stopIdsRef.current, "desc");
        stopIdsRef.current = sortedIds;
        return { ...prev, stops: sortedStops };
      });
    },
    [setColormap],
  );

  useEffect(() => {
    const selectionChanged = prevSelectedColormapIdRef.current !== selectedColormapId;

    if (!selectionChanged && localEditRef.current) {
      localEditRef.current = false;
      return;
    }

    localEditRef.current = false;
    setSortDirection("desc");

    if (selectionChanged) {
      prevSelectedColormapIdRef.current = selectedColormapId;
      stopIdsRef.current = colormap.stops.map(() => makeId());
      setDraftStopValues({});
    } else if (stopIdsRef.current.length !== colormap.stops.length) {
      stopIdsRef.current = colormap.stops.map(() => makeId());
      setDraftStopValues({});
    }

    const normalizedStops = normalizeStops(colormap.stops);
    const { stops: sortedStops, ids } = sortStopsWithIdsFn(normalizedStops, stopIdsRef.current, "desc");
    stopIdsRef.current = ids;
    prevStopsLengthRef.current = sortedStops.length;

    if (!areStopsEqual(colormap.stops, sortedStops)) {
      setColormap((prev) => (areStopsEqual(prev.stops, sortedStops) ? prev : { ...prev, stops: sortedStops }));
    }
  }, [colormap.stops, selectedColormapId, setColormap]);

  useEffect(() => {
    const currentLength = colormap.stops.length;
    const prevLength = prevStopsLengthRef.current;

    if (currentLength > prevLength && !skipAutoScrollRef.current) {
      requestAnimationFrame(() => {
        if (stepsListRef.current) {
          stepsListRef.current.scrollTop = stepsListRef.current.scrollHeight;
        }
      });
    }

    skipAutoScrollRef.current = false;
    prevStopsLengthRef.current = currentLength;
  }, [colormap.stops.length]);

  const setLabel = useCallback(
    (label: string) => {
      setColormap((prev) => ({ ...prev, label }));
    },
    [setColormap],
  );

  const setMode = useCallback(
    (mode: ColormapMode) => {
      setColormap((prev) => ({ ...prev, type: mode }));
    },
    [setColormap],
  );

  const updateStop = useCallback(
    (index: number, next: { value?: number; color?: string }, options?: { sort?: boolean }) => {
      const applyUpdate = options?.sort ? applyStopsUpdateSorted : applyStopsUpdate;
      applyUpdate((prevStops) =>
        prevStops.map((stop, stopIndex) => {
          if (stopIndex !== index) {
            return stop;
          }

          const value = next.value ?? Number(stop[0]);
          const color = next.color ?? String(stop[1]);
          return [Number(value), String(color)];
        }),
      );
    },
    [applyStopsUpdate, applyStopsUpdateSorted],
  );

  const insertStopAt = useCallback(
    (index: number) => {
      skipAutoScrollRef.current = true;
      const nextId = makeId();
      stopIdsRef.current = [...stopIdsRef.current.slice(0, index), nextId, ...stopIdsRef.current.slice(index)];
      applyStopsUpdate((prevStops) => {
        const before = prevStops[index - 1];
        const after = prevStops[index];
        const beforeValue = before ? Number(before[0]) : Number.NaN;
        const afterValue = after ? Number(after[0]) : Number.NaN;
        let nextValue = 0;

        if (Number.isFinite(beforeValue) && Number.isFinite(afterValue)) {
          nextValue = (beforeValue + afterValue) / 2;
        } else if (Number.isFinite(beforeValue)) {
          nextValue = beforeValue - 1;
        } else if (Number.isFinite(afterValue)) {
          nextValue = afterValue + 1;
        }

        const nextColor = before ? String(before[1]) : after ? String(after[1]) : "#000000";
        const nextStop: ColormapCreate["stops"][number] = [Number(nextValue), String(nextColor)];
        const nextStops = prevStops.slice();
        nextStops.splice(index, 0, nextStop);
        return nextStops;
      });
    },
    [applyStopsUpdate],
  );

  const removeStop = useCallback(
    (index: number) => {
      const removedId = stopIdsRef.current[index];
      stopIdsRef.current = stopIdsRef.current.filter((_, stopIndex) => stopIndex !== index);
      if (removedId) {
        setDraftStopValues((prev) => {
          if (!(removedId in prev)) {
            return prev;
          }
          const next = { ...prev };
          delete next[removedId];
          return next;
        });
      }
      applyStopsUpdate((prevStops) => prevStops.filter((_, stopIndex) => stopIndex !== index));
    },
    [applyStopsUpdate],
  );

  const updateStopValueDraft = useCallback((stopId: string, value: string) => {
    setDraftStopValues((prev) => ({ ...prev, [stopId]: value }));
  }, []);

  const clearStopValueDraft = useCallback((stopId: string) => {
    setDraftStopValues((prev) => {
      if (!(stopId in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[stopId];
      return next;
    });
  }, []);

  const commitStopValue = useCallback(
    (index: number) => {
      const stopId = stopIdsRef.current[index] ?? `stop-${index}`;
      const rawValue = draftStopValues[stopId];

      if (rawValue === undefined) {
        return;
      }

      const trimmed = rawValue.trim();
      if (trimmed.length === 0) {
        clearStopValueDraft(stopId);
        return;
      }

      const parsed = Number(trimmed);
      if (!Number.isFinite(parsed)) {
        clearStopValueDraft(stopId);
        return;
      }

      updateStop(index, { value: parsed }, { sort: true });
      clearStopValueDraft(stopId);
    },
    [clearStopValueDraft, draftStopValues, updateStop],
  );

  const swapOrder = useCallback(() => {
    const nextDir = sortDirection === "asc" ? "desc" : "asc";
    const { stops: sortedStops, ids: sortedIds } = sortStopsWithIdsFn(colormap.stops, stopIdsRef.current, "desc");
    const adjustedStops = sortedStops.map((stop, idx) => {
      const mirrored = sortedStops[sortedStops.length - 1 - idx];
      return [stop[0], mirrored[1]] as ColormapCreate["stops"][number];
    });
    setSortDirection(nextDir);
    stopIdsRef.current = sortedIds;
    setColormap((prev) => ({ ...prev, stops: adjustedStops }));
  }, [colormap.stops, sortDirection, setColormap]);

  const handleSave = useCallback(async () => {
    if (isCreating || isUpdating || isLabelInvalid || hasInvalidStops) {
      return;
    }

    const stopsToSave = colormap.stops.slice().sort((a, b) => Number(a[0]) - Number(b[0]));
    const colormapToSave = { ...colormap, stops: stopsToSave };

    try {
      if (activeTab === "edit" && selectedColormapId) {
        await updateColormap({ colormapId: selectedColormapId, data: colormapToSave });
        onSave(selectedColormapId);

        return;
      }

      const newColormap = await createColormap(colormapToSave);
      onSave(newColormap.id);
    } catch (error) {
      console.error("Failed to save colormap:", error);
    }
  }, [
    colormap,
    createColormap,
    isCreating,
    isLabelInvalid,
    isUpdating,
    hasInvalidStops,
    onSave,
    selectedColormapId,
    updateColormap,
  ]);

  return (
    <div className={"container"}>
      <div className={"section"}>
        <label className={"label"} htmlFor="colormap-name-input">
          {t`Custom colormap name`}
        </label>
        <input
          id="colormap-name-input"
          className={"textInput"}
          type="text"
          minLength={1}
          value={colormap.label}
          onChange={(event) => setLabel(event.target.value)}
        />
        {isLabelInvalid ? <span className={"errorText"}>{t`Name is required.`}</span> : null}
      </div>

      <div className={"section"}>
        <div className={"radioGroup"}>
          <label className={"radioOption"}>
            <span>{t`Segmented`}</span>
            <input
              type="radio"
              name="colormap-mode"
              checked={colormap.type === "segmented"}
              onChange={() => setMode("segmented")}
            />
          </label>
          <label className={"radioOption"}>
            <span>{t`Continuous`}</span>
            <input
              type="radio"
              name="colormap-mode"
              checked={colormap.type === "continuous"}
              onChange={() => setMode("continuous")}
            />
          </label>
        </div>
      </div>

      <div className={"section"}>
        <div className={"stepsHeader"}>
          <span>{t`Define color & values steps`}</span>
          <div className={"stepsHeaderActions"}>
            <TooltipWrapper tooltipText={t`Reverse order`} position="top" tail>
              <button className={"headerIconButton"} type="button" onClick={swapOrder} aria-label={t`Sort`}>
                <span className="material-icons">swap_vert</span>
              </button>
            </TooltipWrapper>
          </div>
        </div>
        <div className={"stepsList"} ref={stepsListRef}>
          <div className={"stepInsert"}>
            <button
              className={classNames("stepInsertButton", "top-insert")}
              type="button"
              onClick={() => insertStopAt(0)}
              aria-label={t`Add step`}
            >
              <span className="material-icons">add</span>
            </button>
          </div>
          {colormap.stops.map((stop, idx) => {
            const stopId = stopIdsRef.current[idx] ?? `stop-${idx}`;
            const draftValue = draftStopValues[stopId];
            const displayValue = draftValue ?? String(stop[0]);

            return (
              <Fragment key={stopId}>
                <div className={"stepRow"}>
                  <ColorPickerInput
                    label={""}
                    value={String(stop[1])}
                    change={(color) => updateStop(idx, { color })}
                    data-testid={"color-picker"}
                    showTransparencyGrid={true}
                    withinPortal={false}
                    formFieldOptions={{ alignTop: true, background: false, slimPadding: true }}
                  />
                  <div className={styles["integer-input"]}>
                    <input
                      className={styles["integer-input__input"]}
                      type="number"
                      value={displayValue}
                      onChange={(event) => {
                        const nextRaw = event.target.value;
                        updateStopValueDraft(stopId, nextRaw);
                        const parsed = Number(nextRaw);
                        if (nextRaw.trim().length > 0 && Number.isFinite(parsed)) {
                          updateStop(idx, { value: parsed });
                        }
                      }}
                      onBlur={() => commitStopValue(idx)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.currentTarget.blur();
                        }
                      }}
                      step="any"
                      data-testid="threshold-value"
                    />
                  </div>
                  <button
                    className={"deleteButton"}
                    type="button"
                    onClick={() => removeStop(idx)}
                    aria-label={t`Delete step`}
                  >
                    <span className="material-icons">delete</span>
                  </button>
                </div>
                {idx < colormap.stops.length - 1 ? (
                  <div className={"stepInsert"}>
                    <button
                      className={"stepInsertButton"}
                      type="button"
                      onClick={() => insertStopAt(idx + 1)}
                      aria-label={t`Add step`}
                    >
                      <span className="material-icons">add</span>
                    </button>
                  </div>
                ) : null}
              </Fragment>
            );
          })}
          <div className={"stepInsert"}>
            <button
              className={"stepInsertButton"}
              type="button"
              onClick={() => insertStopAt(colormap.stops.length)}
              aria-label={t`Add step`}
            >
              <span className="material-icons">add</span>
            </button>
          </div>
        </div>
        {areStopsInvalid ? <span className={"errorText"}>{t`At least two steps are required.`}</span> : null}
        {hasInvalidStopValue ? <span className={"errorText"}>{t`Each step needs a valid number.`}</span> : null}
        {hasInvalidStopColor ? <span className={"errorText"}>{t`Each step needs a valid color.`}</span> : null}
      </div>

      <ButtonBar align="left" className={"footer"}>
        <Button onClick={handleSave} primary disabled={isCreating || isUpdating || isLabelInvalid || hasInvalidStops}>
          {isCreating || isUpdating ? t`Saving...` : activeTab === "edit" ? t`Update Colormap` : t`Create Colormap`}
        </Button>
      </ButtonBar>
    </div>
  );
}
